/*
 * Copyright (c) CIRROM 2025.
 */
import React, { lazy } from "react";
import { LazyWrapper } from "../LazyWrapper";
import type {
  RouteContext,
  EntityType,
  RouteConfig,
  SystemOrProject,
} from "./routeTypes";
import { CirromDashboard } from "../../pages/CirromDashboard";
import { UserActivityDashboard } from "../../pages/UserActivityDashboard";
import { ContactMonitorDashboard } from "../../pages/monitor/ContactMonitorDashboard";
import { ContactDashboard } from "../../pages/ContactDashboard";
import { AccountDashboard } from "../../pages/AccountDashboard";

// Lazy load components
const ChangedFieldsPage = lazy(() =>
  import("../../pages/data-quality/ChangedFields").then((module) => ({
    default: module.ChangedFieldsPage,
  }))
);
const RejectedFieldsPage = lazy(() =>
  import("../../pages/data-quality/RejectedFields").then((module) => ({
    default: module.RejectedFieldsPage,
  }))
);
const DuplicatesPage = lazy(() =>
  import("../../pages/data-quality/Duplicates").then((module) => ({
    default: module.DuplicatesPage,
  }))
);
const AccountDuplicatesPage = lazy(() =>
  import("../../pages/data-quality/AccountDuplicates").then((module) => ({
    default: module.AccountDuplicatesPage,
  }))
);

const AddressDuplicatesPage = lazy(() =>
  import("../../pages/data-quality/Addresses").then((module) => ({
    default: module.Addresses,
  }))
);

const CirromMonitorRedirectToFirstItem = lazy(() =>
  import("../../pages/monitor/CirromMonitor").then((module) => ({
    default: module.CirromMonitorRedirectToFirstItem,
  }))
);
const NameChangesPage = lazy(() =>
  import("../../pages/data-quality/NameChanges").then((module) => ({
    default: module.NameChangesPage,
  }))
);
const EmailChangesPage = lazy(() =>
  import("../../pages/data-quality/EmailChanges").then((module) => ({
    default: module.EmailChangesPage,
  }))
);
const LocationChangesPage = lazy(() =>
  import("../../pages/data-quality/LocationChanges").then((module) => ({
    default: module.LocationChangesPage,
  }))
);
const EmploymentChangesPage = lazy(() =>
  import("../../pages/data-quality/EmploymentChanges").then((module) => ({
    default: module.EmploymentChangesPage,
  }))
);
const CirromMonitorLinkage = lazy(() =>
  import("../../pages/monitor/CirromMonitorLinkage").then((module) => ({
    default: module.CirromMonitorLinkage,
  }))
);
const MonitorNameChangesPage = lazy(() =>
  import("../../pages/monitor/MonitorNameChanges").then((module) => ({
    default: module.MonitorNameChangesPage,
  }))
);
const MonitorEmploymentChangesPage = lazy(() =>
  import("../../pages/monitor/MonitorEmploymentChanges").then((module) => ({
    default: module.MonitorEmploymentChangesPage,
  }))
);
const MonitorLocationChangesPage = lazy(() =>
  import("../../pages/monitor/MonitorLocationChanges").then((module) => ({
    default: module.MonitorLocationChangesPage,
  }))
);

const IntegratedSystemsRules = lazy(() =>
  import("../../pages/data-quality/IntegratedSystemsRules").then((module) => ({
    default: module.IntegratedSystemsRules,
  }))
);

// ============================================================================
// Types
// ============================================================================

interface RouteElementConfig {
  Component: React.ComponentType<any>;
  systems: SystemOrProject[];
  setSelectedSystemId: (id: number) => void;
  isDataProject: boolean;
  useLazyWrapper?: boolean;
  additionalProps?: Record<string, any>;
}

interface RouteCreationConfig {
  basePath: "/product" | "/data-project";
  systems: SystemOrProject[];
  setSelectedSystemId: (id: number) => void;
  isDataProject: boolean;
}

export interface CommonRoutesConfig {
  basePath: "/product" | "/data-project";
  userSystems: SystemOrProject[];
  isDataProject: boolean;
  context: RouteContext;
  setSelectedSystemId: (id: number) => void;
  includeContactMonitorRedirect?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Creates a React element with consistent props structure.
 * Centralizes the createElement pattern used throughout route factories.
 */
function createRouteElement(config: RouteElementConfig): React.ReactNode {
  const {
    Component,
    systems,
    setSelectedSystemId,
    isDataProject,
    useLazyWrapper = true,
    additionalProps = {},
  } = config;

  const element = React.createElement(Component, {
    userSystems: systems,
    setSelectedSystemId,
    isDataProject,
    ...additionalProps,
  });

  return useLazyWrapper
    ? React.createElement(LazyWrapper, null, element)
    : element;
}

/**
 * Creates a single route configuration.
 */
function createRoute(
  path: string,
  Component: React.ComponentType<any>,
  config: RouteCreationConfig,
  additionalProps: Record<string, any> = {},
  useLazyWrapper = true
): RouteConfig {
  return {
    path,
    element: createRouteElement({
      Component,
      systems: config.systems,
      setSelectedSystemId: config.setSelectedSystemId,
      isDataProject: config.isDataProject,
      useLazyWrapper,
      additionalProps,
    }),
  };
}

/**
 * Creates routes for specified entities (CONTACT, ACCOUNT, or both).
 * Replaces createFullEntityMatrix and createEntityRoutePair.
 */
function createEntityRoutes(
  pathTemplate: string,
  Component: React.ComponentType<any>,
  config: RouteCreationConfig,
  entities: EntityType[] = ["CONTACT", "ACCOUNT"]
): RouteConfig[] {
  return entities.map((entity) => {
    const path = pathTemplate.replace("{entity}", entity.toLowerCase());
    return createRoute(path, Component, config, { dataEntity: entity });
  });
}

// ============================================================================
// Feature Route Groups
// ============================================================================

/**
 * Creates dashboard and insights routes.
 */
function createDashboardRoutes(config: RouteCreationConfig): RouteConfig[] {
  const { basePath } = config;

  return [
    createRoute(
      `${basePath}/dashboard/:integratedSystemId`,
      CirromDashboard,
      config,
      {},
      false
    ),
    createRoute(
      `${basePath}/all-data/contact/dashboard/:integratedSystemId`,
      ContactDashboard,
      config,
      {},
      false
    ),
    createRoute(
      `${basePath}/all-data/account/dashboard/:integratedSystemId`,
      AccountDashboard,
      config,
      {},
      false
    ),
    createRoute(
      `${basePath}/insights/:integratedSystemId`,
      UserActivityDashboard,
      config,
      {},
      false
    ),
    createRoute(`${basePath}/rules/`, IntegratedSystemsRules, config, {}, true),
  ];
}

/**
 * Creates all-data routes (name, email, location, employment changes).
 */
function createAllDataRoutes(config: RouteCreationConfig): RouteConfig[] {
  const { basePath } = config;

  return [
    // Name changes - both entities
    ...createEntityRoutes(
      `${basePath}/all-data/{entity}/name/:integratedSystemId`,
      NameChangesPage,
      config
    ),
    // Email changes - contact only
    ...createEntityRoutes(
      `${basePath}/all-data/{entity}/email/:integratedSystemId`,
      EmailChangesPage,
      config,
      ["CONTACT"]
    ),

    // Location changes - both entities
    ...createEntityRoutes(
      `${basePath}/all-data/{entity}/location/:integratedSystemId`,
      LocationChangesPage,
      config
    ),
    // Employment changes - contact only
    ...createEntityRoutes(
      `${basePath}/all-data/{entity}/employment/:integratedSystemId`,
      EmploymentChangesPage,
      config,
      ["CONTACT"]
    ),
    // Changed fields
    createRoute(
      `${basePath}/changed-fields/:integratedSystemId`,
      ChangedFieldsPage,
      config
    ),
    // Rejected fields
    createRoute(
      `${basePath}/rejected-fields/:integratedSystemId`,
      RejectedFieldsPage,
      config
    ),
  ];
}

/**
 * Creates duplicates and address routes.
 */
function createDuplicatesRoutes(config: RouteCreationConfig): RouteConfig[] {
  const { basePath } = config;

  return [
    createRoute(
      `${basePath}/contact/duplicates/:integratedSystemId/*`,
      DuplicatesPage,
      config
    ),
    createRoute(
      `${basePath}/account/duplicates/:integratedSystemId`,
      AccountDuplicatesPage,
      config
    ),
    createRoute(
      `${basePath}/account/address/:integratedSystemId`,
      AddressDuplicatesPage,
      config
    ),
  ];
}

/**
 * Creates contact monitor routes.
 */
function createContactMonitorRoutes(
  config: RouteCreationConfig,
  includeRedirect: boolean
): RouteConfig[] {
  const { basePath } = config;
  const routes: RouteConfig[] = [];

  // Optional redirect route (only for integrated systems)
  if (includeRedirect) {
    routes.push(
      createRoute(
        `${basePath}/contact-monitor`,
        CirromMonitorRedirectToFirstItem,
        config
      )
    );
  }

  // Dashboard
  routes.push(
    createRoute(
      `${basePath}/contact-monitor/:integratedSystemId`,
      ContactMonitorDashboard,
      config,
      {},
      false
    )
  );

  // Sub-routes
  routes.push(
    createRoute(
      `${basePath}/contact-monitor/linking/:integratedSystemId`,
      CirromMonitorLinkage,
      config
    ),
    createRoute(
      `${basePath}/contact-monitor/name/:integratedSystemId`,
      MonitorNameChangesPage,
      config
    ),
    createRoute(
      `${basePath}/contact-monitor/company/:integratedSystemId`,
      MonitorEmploymentChangesPage,
      config
    ),
    createRoute(
      `${basePath}/contact-monitor/location/:integratedSystemId`,
      MonitorLocationChangesPage,
      config
    )
  );

  return routes;
}

// ============================================================================
// Main Export
// ============================================================================

/**
 * Generates common routes shared between integrated systems and data projects.
 * This is the main entry point called from Routes.tsx.
 */
export function createCommonRoutes(config: CommonRoutesConfig): RouteConfig[] {
  const {
    basePath,
    userSystems,
    isDataProject,
    setSelectedSystemId,
    includeContactMonitorRedirect = false,
  } = config;

  const routeConfig: RouteCreationConfig = {
    basePath,
    systems: userSystems,
    setSelectedSystemId,
    isDataProject,
  };

  return [
    ...createDashboardRoutes(routeConfig),
    ...createAllDataRoutes(routeConfig),
    ...createDuplicatesRoutes(routeConfig),
    ...createContactMonitorRoutes(routeConfig, includeContactMonitorRedirect),
  ];
}
